import {
  configDefaultTransport,
  warn,
} from './logger'

import {
  configure as configureMakeUri,
  makeUri,
  getMatchedPath,
} from './makeUri'

import {
  HTTPClientError,
  HTTPServerError,
  HTTPBizError,
  getErrorCodeFromData,
  getErrorMsgFromData,
  DEFAULT_ERROR_CODE_PROP,
  DEFAULT_ERROR_MSG_PROP,
} from './errors'

import { createInterceptor } from './interceptor'

import {
  transformRequestConfig,
  transformResponseData,
} from './dataTransform'

import { mapStatusCodeFromLegacyResponse } from './legacyCode'

import {
  merge,
  isFormData,
  isNumber,
} from './util'

import apiListAdapter from './apiListAdapter'

import {
  DEFAULT_TRACE_HOST_LIST, generateTraceId, TRACE_HEADER_NAME,
} from './trace'

/* --------------------------------- */

let defaultConfig = {}

function configure(config) {
  if (config.baseURL || config.apiList) {
    configureMakeUri({
      baseURL: config.baseURL,
      apiList: apiListAdapter(config.apiList),
    })
  }

  if (config.defaults) {
    defaultConfig = config.defaults
  }

  if (config.logger) {
    configDefaultTransport(config.logger)
  }
}

/* --------------------------------- */

function isConfigWithFormData(config) {
  return typeof config === 'object' && isFormData(config.data)
}

function makeSendingConfig(originalConfig) {
  const finalConfig = merge(defaultConfig, originalConfig)

  if (isConfigWithFormData(finalConfig)
    && !isNumber(originalConfig.timeout)
    && isNumber(defaultConfig.uploadTimeout)) {
    // if it is formdata and no specific timeout, then use default upload timeout
    finalConfig.timeout = defaultConfig.uploadTimeout
  }

  if (finalConfig.uploadTimeout) {
    delete finalConfig.uploadTimeout // delete the unstandard config value
  }

  return finalConfig
}

function handleResData(resData, extractData) {
  // customize extraction method, uses only if necessary
  if (extractData === false) {
    return resData
  } if (typeof extractData === 'function') {
    return extractData(resData)
  }

  return resData.data
}

function processSend(send, originalConfig, interceptors) {
  let config = originalConfig
  let response = null

  const setupFinalConfig = {
    fulfilled: inChainConfig => {
      // the config content will be change during processes, so keep the reference in the top of function
      if (inChainConfig !== config) {
        warn('[Http Warning] config reference has been changed during request interceptor')
      }

      config = transformRequestConfig(makeSendingConfig(inChainConfig))
      return config
    },
  }

  const requestHandler = {
    fulfilled: inChainConfig => {
      // the config content will be change during processes, so keep the reference in the top of function
      if (inChainConfig !== config) {
        warn('[Http Warning] config reference has been changed during dispatch interceptor')
      }

      const sendPromise = send(inChainConfig)

      if (!sendPromise || !sendPromise.then) {
        throw new Error('[Http Exception] send must return a promise object')
      }

      return sendPromise
    },
  }

  const responseStatusCodeHandler = {
    name: 'responseStatusCodeHandler',
    fulfilled: res => {
      // for some legacy api, which will use "result" to mark the api error
      // , like: { result: -101 }
      // , so map to standard status code
      if (res.status === 200) {
        res.status = mapStatusCodeFromLegacyResponse(res.data)
      }

      if (Object.prototype.toString.call(res.data) === '[object Object]') {
        res.data[DEFAULT_ERROR_CODE_PROP] = getErrorCodeFromData(res.data)
        res.data[DEFAULT_ERROR_MSG_PROP] = getErrorMsgFromData(res.data)
      }

      response = res

      if (res.status < 200 || res.status >= 300) {
        throw new HTTPServerError(res.status, res.data)
      }


      return res.data
    },
    rejected: err => {
      throw new HTTPClientError(err.code ?? 'ENetworkError', err.message)
    },
  }

  const responseDataHandler = {
    fulfilled: (resData, res) => {
      if (resData.success !== undefined && Boolean(resData.success) === false) {
        throw new HTTPBizError(res.status, resData)
      }

      const parsedData = transformResponseData(resData, config)

      return handleResData(parsedData, config.extractData)
    },
  }

  const chain = [
    ...interceptors.request.queue,
    setupFinalConfig,
    ...interceptors.dispatch.queue,
    requestHandler,
    ...interceptors.spam.queue,
    responseStatusCodeHandler,
    ...interceptors.response.queue,
    responseDataHandler,
    ...interceptors.result.queue,
  ]

  let promise = Promise.resolve(config)

  const wrapFulfilled = handler => payload => (handler ? handler(payload, response, config) : payload)

  const wrapRejected = handler => ex => {
    if (handler) {
      // 由 rejected handler 控制异常流程，参见 !19
      return handler(ex, response, config)
    }

    throw ex
  }

  while (chain.length) {
    const task = chain.shift()

    promise = promise.then(wrapFulfilled(task.fulfilled), wrapRejected(task.rejected))
  }

  return promise
}

const POST_METHODS = ['POST', 'PUT', 'PATCH']
const GET_METHODS = ['GET', 'DELETE', 'HEAD']

const sendFactory = (method, request, interceptors, withData) => (apiKeyOrUri, arg1, arg2) => {
  if (!apiKeyOrUri) {
    return Promise.reject(
      new Error('[Http Exception] apiKey is empty')
    )
  }

  let config

  if (withData) {
    // call like post('api', data, config)
    config = arg2 || {}
    config.data = arg1
  } else {
    // call like get('api', config)
    config = arg1 || {}

    // for some legacy cases, it may be call like get('api', config, config), just handle it
    if (typeof arg2 === 'object') {
      Object.assign(config, arg2)
    }
  }

  config.method = method
  config.url = makeUri(apiKeyOrUri, config)
  config.matchedPath = getMatchedPath(apiKeyOrUri)

  if (!config.headers) {
    config.headers = {}
  }
  if (!config.headers[TRACE_HEADER_NAME]) {
    // 相对路径直接加上 traceId
    if (config.url.indexOf('/') === 0 && config.url.indexOf('//') !== 0) {
      config.headers[TRACE_HEADER_NAME] = generateTraceId()
    } else {
      let traceHostList = DEFAULT_TRACE_HOST_LIST

      if (defaultConfig.traceHosts) {
        traceHostList = traceHostList.concat(defaultConfig.traceHosts)
      }
      traceHostList.some(host => {
        if (config.url.includes(host)) {
          config.headers[TRACE_HEADER_NAME] = generateTraceId()

          return true
        }
        return false
      })
    }
  }

  return processSend(request, config, interceptors)
}

export default function factory(request) {
  const http = {}

  const interceptors = {
    request: createInterceptor(), // before send
    dispatch: createInterceptor(), // before send
    spam: createInterceptor(), // handle spam plugins
    response: createInterceptor(), // before handle response
    result: createInterceptor(),
  }

  POST_METHODS.forEach(method => {
    http[method.toLowerCase()] = sendFactory(
      method, request, interceptors, true
    )
  })

  GET_METHODS.forEach(method => {
    http[method.toLowerCase()] = sendFactory(
      method, request, interceptors, false
    )
  })

  http.del = http['delete'] //eslint-disable-line

  http.configure = configure
  http.interceptors = interceptors
  http.makeUri = makeUri

  // TODO, add cancellable method

  return http
}
